import { Button, Typography } from "@/components/atoms";
import { GuidanceNotice } from "./GuidanceNotice";
import { ResponsiveDialog } from "./ResponsiveDialog";

interface AddCardIntroModalProps {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
}

/**
 * Speed bump between "Add credit card" and the Stripe form: a new card never moves
 * existing bookings off the old one — editing the existing card does, because saving it repoints
 * that card's unpaid jobs onto the replacement. Only shown once the account already has a card;
 * the very first card has nothing to replace.
 */
export const AddCardIntroModal = ({ open, onClose, onContinue }: AddCardIntroModalProps) => {
  const renderButtons = (isMobile: boolean) => {
    const continueButton = (
      <Button variant="primary" size="lg" onClick={onContinue} className={isMobile ? "w-full" : "flex-1"}>
        Continue
      </Button>
    );
    const cancelButton = isMobile ? (
      <button type="button" className="h-10 w-full" onClick={onClose}>
        <Typography variant="action" className="text-info">
          Cancel
        </Typography>
      </button>
    ) : (
      <Button variant="secondary" size="lg" onClick={onClose} className="flex-1">
        Cancel
      </Button>
    );

    return isMobile ? (
      <>
        {continueButton}
        {cancelButton}
      </>
    ) : (
      <>
        {cancelButton}
        {continueButton}
      </>
    );
  };

  return (
    <ResponsiveDialog open={open} title="Add a credit card" onClose={onClose} renderButtons={renderButtons}>
      <GuidanceNotice
        tone="info"
        variant="feature"
        icon={{ name: "help-circle", isCustom: true }}
        title="Adding a new card won't update existing bookings."
      >
        If you&apos;re replacing an expired, declined, or unavailable card, update the existing card instead. Adding a new card will only
        apply to new bookings.
      </GuidanceNotice>
    </ResponsiveDialog>
  );
};
